import React, { useState } from 'react';
import { X, Cake, UserCircle2, ChevronRight } from 'lucide-react';
import { useTeamBirthdays } from '../../../../hooks/manager_portal_hooks';
import TeamMemberDetailModal from '../../TeamMemberDetailModal';

/**
 * Team Birthdays Modal
 *
 * Opened from the "Team member birthdays this month" card on the Manager
 * Dashboard. Lists the manager's team members whose BirthDate on
 * `/data/EmployeesV2` falls in the current calendar month (sourced from
 * `useTeamBirthdays` — the same query that feeds the card count, so no
 * additional API call is made here).
 *
 * Selecting a member opens the shared TeamMemberDetailModal, keeping the
 * drill-down consistent with the other dashboard modals and the "My Team" tab.
 */
const statusStyle = (status) =>
  status === 'On Leave'
    ? { bg: 'bg-yellow-50', text: 'text-yellow-700', ring: 'ring-yellow-200' }
    : { bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-200' };

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Format a birthday as "15 Jul" — day + short month, no year (a birthday is
 * recurring, so the year is not meaningful here). Read in UTC: BirthDate is a
 * calendar date, not a point in time, and useTeamBirthdays already filters by
 * UTC month/day — reading it back with local getters here could show a
 * different day than what qualified it for this list.
 */
const formatBirthday = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()]}`;
};

const TeamBirthdaysModal = ({ onClose }) => {
  const { birthdays, isLoading, error } = useTeamBirthdays();
  const [selectedMember, setSelectedMember] = useState(null);

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Team member birthdays this month"
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                <Cake className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Birthdays This Month</h3>
                <p className="text-sm text-gray-500">
                  {isLoading
                    ? 'Loading…'
                    : `${birthdays.length} birthday${birthdays.length === 1 ? '' : 's'} this month`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable list */}
          <div className="overflow-y-auto flex-1 p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-pink-500"></div>
                <p className="ml-3 text-gray-600 text-sm">Loading birthdays…</p>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-red-500 text-sm">Failed to load birthdays. Please try again.</p>
              </div>
            ) : birthdays.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500 text-sm">No team member birthdays this month.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {birthdays.map((member) => {
                  const s = statusStyle(member.status);
                  const bday = formatBirthday(member.birthDate);
                  return (
                    <li key={member.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedMember(member)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-pink-200 hover:bg-pink-50/40 transition-colors text-left"
                      >
                        <div className="w-9 h-9 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                          <UserCircle2 className="w-5 h-5 text-pink-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                          <p className="text-xs text-gray-500 truncate">{member.role}</p>
                        </div>
                        {bday && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 bg-pink-50 text-pink-700 ring-pink-200">
                            <Cake className="w-3 h-3" />
                            {bday}
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${s.bg} ${s.text} ${s.ring}`}>
                          {member.status}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {selectedMember && (
        <TeamMemberDetailModal
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </>
  );
};

export default TeamBirthdaysModal;
